import {
	ActionIcon,
	Badge,
	Indicator,
	Menu,
	ScrollArea,
	Text,
} from "@mantine/core";
import { IconBell, IconBug, IconSparkles, IconTool } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { type ChangelogEntry, changelog } from "../../data/changelog";

const WHATS_NEW_LAST_SEEN_KEY = "relatance-whats-new-last-seen";

function ChangelogItem({
	entry,
	isNew,
}: {
	entry: ChangelogEntry;
	isNew: boolean;
}) {
	const typeIcon = {
		feature: <IconSparkles size={14} />,
		improvement: <IconTool size={14} />,
		fix: <IconBug size={14} />,
	}[entry.type];

	return (
		<Menu.Item leftSection={typeIcon}>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<Text size="sm" fw={500} truncate>
						{entry.title}
					</Text>
					<Text size="xs" c="dimmed">
						{entry.description}
					</Text>
				</div>
				{isNew && (
					<Badge size="xs" color="blue" className="shrink-0">
						New
					</Badge>
				)}
			</div>
		</Menu.Item>
	);
}

export function WhatsNew() {
	const [lastSeen, setLastSeen] = useState<Date>(() => {
		try {
			const stored = localStorage.getItem(WHATS_NEW_LAST_SEEN_KEY);
			return stored ? new Date(stored) : new Date(0);
		} catch {
			return new Date(0);
		}
	});

	const hasUnread = useMemo(
		() => changelog.some((entry) => entry.date > lastSeen),
		[lastSeen],
	);

	const handleOpen = () => {
		const now = new Date();
		setLastSeen(now);
		try {
			localStorage.setItem(WHATS_NEW_LAST_SEEN_KEY, now.toISOString());
		} catch {
			// Ignore storage errors
		}
	};

	return (
		<Menu onOpen={handleOpen} width={320} position="bottom-end">
			<Menu.Target>
				<Indicator disabled={!hasUnread} color="blue" size={8} offset={4}>
					<ActionIcon
						variant="subtle"
						size="lg"
						aria-label="What's new"
						c={hasUnread ? undefined : "dimmed"}
					>
						<IconBell size={20} />
					</ActionIcon>
				</Indicator>
			</Menu.Target>
			<Menu.Dropdown>
				<Menu.Label>What's New</Menu.Label>
				<ScrollArea.Autosize mah={400}>
					{changelog.map((entry) => (
						<ChangelogItem
							key={entry.id}
							entry={entry}
							isNew={entry.date > lastSeen}
						/>
					))}
				</ScrollArea.Autosize>
			</Menu.Dropdown>
		</Menu>
	);
}
